import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';

const glass = css`
  backdrop-filter: blur(26px) saturate(175%) brightness(1.03);
  -webkit-backdrop-filter: blur(26px) saturate(175%) brightness(1.03);

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04)) padding-box,
    linear-gradient(135deg, rgba(246,201,69,0.55), rgba(255,255,255,0.18), rgba(139,29,29,0.16)) border-box;

  box-shadow:
    0 18px 55px rgba(0,0,0,0.12),
    inset 0 1px 0 rgba(255,255,255,0.35);
`;

const glassSoft = css`
  backdrop-filter: blur(18px) saturate(165%) brightness(1.02);
  -webkit-backdrop-filter: blur(18px) saturate(165%) brightness(1.02);

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.03)) padding-box,
    linear-gradient(135deg, rgba(246,201,69,0.45), rgba(255,255,255,0.16), rgba(139,29,29,0.14)) border-box;

  box-shadow:
    0 14px 45px rgba(0,0,0,0.10),
    inset 0 1px 0 rgba(255,255,255,0.28);
`;

const PricingSection = ({ isOpen, onToggle, hideStandalone = false }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const modalOpen = typeof isOpen === 'boolean' ? isOpen : localOpen;
  const toggle = onToggle ?? (() => setLocalOpen((prev) => !prev));

  return (
    <>
      {!hideStandalone && (
        <PricingContainer id="pricing">
          <Top>
            <PricingTitle>Pricing</PricingTitle>
            <PricingSubTitle>
              Choose a plan that fits your Baybayin learning and recognition needs.
            </PricingSubTitle>

            <PricingToggleButton onClick={toggle} aria-pressed={modalOpen}>
              {modalOpen ? 'Hide Pricing' : 'View Pricing'}
            </PricingToggleButton>
          </Top>
        </PricingContainer>
      )}

      {modalOpen && (
        <PricingModalOverlay onClick={toggle}>
          <PricingModal onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={toggle} aria-label="Close pricing">
              ×
            </ModalCloseButton>

            <ModalHeader>
              <PricingTitle>Pricing</PricingTitle>
              <PricingSubTitle>
                Choose a plan that fits your Baybayin learning and recognition needs.
              </PricingSubTitle>
            </ModalHeader>

            <PricingCards>
              <Card $highlighted>
                <CardHeader $highlighted>
                  <CardTitle>Free</CardTitle>
                  <CardPrice>₱0/month</CardPrice>
                </CardHeader>

                <CardBody>
                  <CardFeatureList>
                    <CardFeature>
                      <FaCheckCircle /> Individual Character Recognition
                    </CardFeature>
                  </CardFeatureList>
                </CardBody>

                <ButtonContainer>
                  <CardButton disabled>Current Plan</CardButton>
                </ButtonContainer>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Premium</CardTitle>
                  <CardPrice>₱289/month</CardPrice>
                </CardHeader>

                <CardBody>
                  <CardFeatureList>
                    <CardFeature>
                      <FaCheckCircle /> Complete Word Recognition
                    </CardFeature>
                  </CardFeatureList>
                </CardBody>

                <ButtonContainer>
                  <CardButton>Get Premium</CardButton>
                </ButtonContainer>
              </Card>
            </PricingCards>
          </PricingModal>
        </PricingModalOverlay>
      )}
    </>
  );
};

export default PricingSection;

/* ---------- Styles ---------- */

const PricingContainer = styled.section`
  min-height: 100vh;
  padding: 6.5rem 2rem 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  /* IMPORTANT: remove the bright gradient you had */
  background: transparent;

  /* subtle blobs to help glass read (brand colors) */
  &::before {
    content: '';
    position: absolute;
    width: 680px;
    height: 680px;
    left: -260px;
    top: 120px;
    background: radial-gradient(circle at 35% 35%, rgba(139,29,29,0.18), transparent 62%);
    filter: blur(60px);
    opacity: 0.9;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    width: 720px;
    height: 720px;
    right: -300px;
    bottom: -300px;
    background: radial-gradient(circle at 40% 40%, rgba(246,201,69,0.16), transparent 62%);
    filter: blur(70px);
    opacity: 0.9;
    pointer-events: none;
  }
`;

const Top = styled.div`
  position: relative;
  z-index: 1;
  margin-bottom: 2.2rem;
`;

const PricingTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-weight: 1000;
  font-size: clamp(2rem, 3.2vw, 2.6rem);
  color: #ffffff;
  margin: 0 0 0.45rem;
  letter-spacing: -0.02em;
`;

const PricingSubTitle = styled.p`
  margin: 0 auto;
  max-width: 1000px;
  font-family: 'Poppins', sans-serif;
  color: #ffffff;
  font-size: 1.05rem;
`;

const PricingCards = styled.div`
  position: relative;
  z-index: 1;

  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 520px));
  justify-content: center;
  gap: 1.6rem;
  align-items: stretch;

  @media (max-width: 980px) {
    grid-template-columns: minmax(290px, 570px);
  }
`;

const Card = styled.div`
  ${glass}
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      border: 1px solid transparent;
      background:
        linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.05)) padding-box,
        linear-gradient(135deg, rgba(246,201,69,0.80), rgba(139,29,29,0.20)) border-box;
      box-shadow:
        0 22px 70px rgba(0,0,0,0.14),
        inset 0 1px 0 rgba(255,255,255,0.38);
    `}

  &:hover {
    transform: translateY(-4px);
    filter: brightness(1.02);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem 1.50rem 1.5rem;
  text-align: left;

  ${({ $highlighted }) =>
    $highlighted
      ? css`
          background: linear-gradient(
            135deg,
            rgba(139,29,29,0.85),
            rgba(111,20,20,0.75)
          );
          color: #ffffff;
        `
      : css`
          ${glassSoft}
          border: 0;
          border-radius: 0;
          color: rgba(0, 0, 0, 0.85);
        `}
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  font-size: 1.6rem;
  letter-spacing: -0.02em;
`;

const CardPrice = styled.p`
  margin: 0.35rem 0 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  opacity: 0.92;
`;

const CardBody = styled.div`
  padding: 1.25rem 1.25rem 0.6rem;
  text-align: left;
  flex: 1;
`;

const CardFeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CardFeature = styled.li`
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  color: rgba(1, 1, 1, 0.82);
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  line-height: 1.35;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: rgba(139, 29, 29, 0.95);
    filter: drop-shadow(0 6px 18px rgba(0,0,0,0.12));
  }
`;

const ButtonContainer = styled.div`
  padding: 1.1rem 1.25rem 1.35rem;
`;

const CardButton = styled.button`
  ${glassSoft}
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 14px;

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04)) padding-box,
    linear-gradient(135deg, rgba(246,201,69,0.55), rgba(139,29,29,0.20)) border-box;

  color: #8b1d1d;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  font-size: 1.05rem;
  cursor: pointer;

  transition: transform 0.16s ease, filter 0.16s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.03);
  }

  &:active {
    transform: translateY(0px) scale(0.99);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    filter: none;
    transform: none;
  }
`;

const PricingToggleButton = styled.button`
  ${glassSoft}
  margin-top: 1.35rem;
  border-radius: 999px;
  padding: 0.9rem 2.3rem;
  border: 1px solid transparent;
  color: #8b1d1d;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.16s ease, filter 0.16s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.04);
  }

  &:active {
    transform: translateY(0px) scale(0.985);
  }
`;

const PricingModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 6, 6, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  padding: 1.2rem;
  z-index: 9999;
`;

const PricingModal = styled.div`
  ${glass}
  width: min(1040px, 100%);
  max-height: 90vh;
  overflow: auto;
  border-radius: 28px;
  padding: 2.2rem 2rem 2.4rem;
  position: relative;
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.8rem;

  ${PricingSubTitle} {
    margin-top: 0.35rem;
  }
`;

const ModalCloseButton = styled.button`
  ${glassSoft}
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  border: 1px solid transparent;
  font-size: 1.35rem;
  font-weight: 900;
  cursor: pointer;
  transition: filter 0.16s ease;

  &:hover {
    filter: brightness(1.04);
  }

  &:active {
    transform: translateY(0px) scale(0.98);
  }
`;
